import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { useAuth, useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast } from "@/comn/hooks";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import {
    BASE,
    URLS,
    APIS,
    SF_DOC_TRMSSN_MGMT_INFO,
} from "@/tra/tancis/ptli/elctdoc/trmssn/services/PtliDocTrmssnMgmtService";
import { SG_RESD_HIST_LIST } from "@/tra/tancis/ptli/elctdoc/rcpn/services/PtliDocRcpnMgmtService";
import { PTLI0701003Q } from "@/tra/tancis/ptli/elctdoc/rcpn/PTLI0701003Q";

/*
 * Document transmission history detail information
 * !== 전자문서 발신 이력 상세정보 ==!
 */
export const PTLI0702003Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0047", "auth_ptli0004", "auth_ptli0049", "auth_ptli0015", "auth_ptlianyADM", "auth_ptli0048"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const modal = useModal();
    const { trscLogId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        docTrmssnMgmtInfo: useForm({
            defaultSchema: SF_DOC_TRMSSN_MGMT_INFO,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    const grid = {
        resdHistList: useGrid({
            defaultSchema: SG_RESD_HIST_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const fetch = {
        // Gets Document Transmission information. !== 전자문서 발신 정보를 조회한다.==!
        getElctDocTrmssnDtl: useFetch({
            api: () => APIS.getElctDocTrmssnDtl(trscLogId),
            enabled: !!trscLogId,
            onSuccess: (data) => {
                const elctDocTrmssn = data?.elctDocTrmssn?.content;

                form.docTrmssnMgmtInfo.setValues(elctDocTrmssn);
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate(URLS.ptli0702001q);
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
        // Update sending electronic documents !== 전자문서 발신 수정 ==!
        updateElctDocTrmssn: useFetch({
            api: (data) => APIS.updateElctDocTrmssn({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.docTrmssnMgmtInfo.setErrors(error.response.data.errors);
                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
        // Get a list of Receive electronic documents. !== 전자문서 수신 목록을 조회한다. ==!
        getResdHistList: useFetch({
            api: (page = grid.resdHistList.page) => APIS.getResdHistList({ trscLogId }, page, grid.resdHistList.size),
            enabled: !!trscLogId,
            key: [grid.resdHistList.page, grid.resdHistList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    page: grid.resdHistList.page,
                    size: grid.resdHistList.size,
                });
            },
            onError: (error) => {
                console.log(error);
                toast.showToast({ type: "error", content: "msg.com.00025" }); // Failed to select. !== 조회가 실패하였습니다. ==!
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // on Save Button Click Event Handler !== 저장 버튼 클릭 ==!
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.docTrmssnMgmtInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.docTrmssnMgmtInfo.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateElctDocTrmssn.fetch(form.docTrmssnMgmtInfo.getValues());
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        getResdHistDtl: (rsndTrscLogId: String) => {
            navigate(`/ptli/elctdoc/trmssn/ptli0702003q/${rsndTrscLogId}`);
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_resdHistList: {
            cell: {
                trscLogId: (props: any) => {
                    const { rowValues, value } = props;
                    return (
                        <Button color="blue" variant="underlined" onClick={() => handler.getResdHistDtl(value)}>
                            {value}
                        </Button>
                    );
                },
            },
        },
    };

    /*
     * Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        form.docTrmssnMgmtInfo.setEditable(false);
        if (trscLogId) {
            fetch.getElctDocTrmssnDtl.fetch();
            grid.resdHistList.setPage(0);
        }
    }, [trscLogId]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.docTrmssnMgmtInfo.schema.trscLogId}
                                controlSize={10}
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfId}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.trscId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfSndrId}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfRcvrId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.coReffNo}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.ucrNo}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfSttsCdNm}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.frstRgsrDtm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.docTrmssnMgmtInfo.schema.intfRsltErrCn}
                                controlSize={10}
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.htphdCn} controlSize={10}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.docTrmssnMgmtInfo.schema.msgOrgnCn}
                                controlSize={10}
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {t("L_MSG_ORGN_CN")}
                                        <Button
                                            role="search"
                                            style={{ display: "inline-block" }}
                                            onClick={() => {
                                                const msg = form.docTrmssnMgmtInfo.getValue("msgOrgnCn");

                                                if (comnUtils.isEmpty(msg)) return;

                                                const modalCallback = () => {
                                                    modal.closeModal();
                                                };

                                                modal.openModal({
                                                    draggable: true,
                                                    size: "md",
                                                    content: <PTLI0701003Q callback={modalCallback} msg={msg} />,
                                                });
                                            }}
                                        >
                                            Beautify
                                        </Button>
                                    </div>
                                }
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.docTrmssnMgmtInfo.schema.msgAckCn}
                                controlSize={10}
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {t("L_MSG_ACK_CN")}
                                        <Button
                                            role="search"
                                            style={{ display: "inline-block" }}
                                            onClick={() => {
                                                const msg = form.docTrmssnMgmtInfo.getValue("msgAckCn");

                                                if (comnUtils.isEmpty(msg)) return;

                                                const modalCallback = () => {
                                                    modal.closeModal();
                                                };

                                                modal.openModal({
                                                    draggable: true,
                                                    size: "md",
                                                    content: <PTLI0701003Q callback={modalCallback} msg={msg} />,
                                                });
                                            }}
                                        >
                                            Beautify
                                        </Button>
                                    </div>
                                }
                            ></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Group>
                        <Group.Body>
                            <Layout>
                                <Layout.Left>
                                    <Group.Title title={"Resend History List"} titleSize={2}></Group.Title>
                                </Layout.Left>
                            </Layout>
                            <Group.Section>
                                <Grid
                                    {...grid.resdHistList.grid}
                                    data={fetch.getResdHistList.data?.resdHistList}
                                    render={render.grid_resdHistList}
                                />
                            </Group.Section>
                        </Group.Body>
                    </Group>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="list"
                                onClick={() => {
                                    navigate(URLS.ptli0702001q);
                                }}
                            ></Button>
                        </Layout.Left>
                        {/* <Layout.Right>
                            <Button
                                role="save"
                                onClick={() => {
                                    handler.onSaveBtnClick();
                                }}
                            >
                            </Button>
                        </Layout.Right> */}
                    </Layout>
                </Group.Body>
            </Group>
        </Page>
    );
};
